import type { ResolvedInstallationOrder } from "../types/installation";

export type InstallationDiscountScope = "installation" | "product";

/** Infer whether coupon applies to installation cost or purchased line items. */
export function inferInstallationDiscountScope(input: {
  discountAppliesTo?: string | null;
  discountCode?: string | null;
  discountAmount?: number | null;
  productDiscountAmount?: number | null;
  lineItems?: Array<{
    is_new_purchase?: boolean;
    discount_code?: string;
    discount?: number;
    discount_on_total_quantity?: number;
  }> | null;
}): InstallationDiscountScope {
  const explicit = String(input.discountAppliesTo ?? "").toLowerCase();
  if (explicit === "product") return "product";
  if (explicit === "installation" || explicit === "job") return "installation";

  if (Number(input.productDiscountAmount ?? 0) > 0) return "product";

  const purchased = (input.lineItems || []).filter((li) => li.is_new_purchase === true);
  const lineCouponTotal = purchased.reduce(
    (s, li) => s + Number(li.discount_on_total_quantity ?? li.discount ?? 0),
    0
  );
  const hasLineDiscountCode = purchased.some((li) =>
    String(li.discount_code ?? "").trim()
  );

  if (hasLineDiscountCode || lineCouponTotal > 0) return "product";

  return "installation";
}

/** Infer scope from POST /orders/resolve response (source of truth after apply). */
export function inferInstallationDiscountScopeFromResolved(
  resolved: ResolvedInstallationOrder | null | undefined
): InstallationDiscountScope {
  if (!resolved) return "installation";

  const purchased = (resolved.line_items || []).filter((li) => li.is_new_purchase !== false);
  const hasLineDiscountCode = purchased.some((li) =>
    String(li.discount_code ?? "").trim()
  );
  const lineCouponTotal = purchased.reduce(
    (s, li) =>
      s +
      Number(
        li.discount_on_total_quantity ??
          (li as { discount?: number }).discount ??
          0
      ),
    0
  );

  if (hasLineDiscountCode || lineCouponTotal > 0) return "product";

  const orderDisc = Number(resolved.discount_amount ?? 0);
  if (orderDisc > 0) return "installation";

  return "installation";
}

/** Root vs line_items for order create/update only — resolve always uses root. */
export function splitInstallationDiscountForPayload(
  discountCode: string,
  scope: InstallationDiscountScope
): { rootDiscountCode: string; lineDiscountCode: string } {
  const code = String(discountCode ?? "").trim();
  if (!code) return { rootDiscountCode: "", lineDiscountCode: "" };
  if (scope === "product") return { rootDiscountCode: "", lineDiscountCode: code };
  return { rootDiscountCode: code, lineDiscountCode: "" };
}

/**
 * POST /orders/resolve: discount_code always on payload root (never on line_items).
 * Falls back to line-item discount_code when GET order only has product-level coupon.
 */
export function getInstallationResolveRootDiscountCode(input: {
  discountCodeOverride?: string;
  formikDiscountCode?: string | null;
  orderDiscountCode?: string | null;
  orderCouponCode?: string | null;
  latestDiscountCodeRef?: string | null;
  orderLineItems?: unknown[] | null;
}): string {
  if (input.discountCodeOverride !== undefined) {
    return String(input.discountCodeOverride).trim();
  }

  const rootCode = String(
    input.formikDiscountCode ??
      input.orderDiscountCode ??
      input.orderCouponCode ??
      input.latestDiscountCodeRef ??
      ""
  ).trim();
  if (rootCode) return rootCode;

  const lineItems = Array.isArray(input.orderLineItems) ? input.orderLineItems : [];
  const lineCode = lineItems.find((raw) => {
    const li = raw as { is_new_purchase?: boolean; discount_code?: string };
    return li.is_new_purchase === true && String(li.discount_code ?? "").trim();
  }) as { discount_code?: string } | undefined;

  return String(lineCode?.discount_code ?? "").trim();
}

export function purchasedResolveLineItemCount(
  orderLineItems: unknown[] | null | undefined
): number {
  return (orderLineItems || []).filter((raw) => {
    const li = raw as { is_new_purchase?: boolean };
    return li.is_new_purchase === true;
  }).length;
}

/**
 * Before scope is known (first apply), prefer line-only when purchased products exist,
 * otherwise root-only for installation coupons.
 */
export function inferInstallationDiscountScopeForResolveRequest(input: {
  discountAppliesTo?: string | null;
  discountCode?: string | null;
  orderLineItems?: unknown[] | null;
  resolvedLineItems?: ResolvedInstallationOrder["line_items"] | null;
}): InstallationDiscountScope {
  const code = String(input.discountCode ?? "").trim();
  if (!code) return "installation";

  if (input.discountAppliesTo) {
    return inferInstallationDiscountScope({
      discountAppliesTo: input.discountAppliesTo,
      discountCode: code,
      lineItems: (input.resolvedLineItems || []) as Array<{
        is_new_purchase?: boolean;
        discount_code?: string;
        discount?: number;
        discount_on_total_quantity?: number;
      }>,
    });
  }

  if (input.resolvedLineItems?.length) {
    return inferInstallationDiscountScopeFromResolved({
      line_items: input.resolvedLineItems,
    } as ResolvedInstallationOrder);
  }

  if (purchasedResolveLineItemCount(input.orderLineItems) > 0) {
    return "product";
  }

  return "installation";
}
